import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts, ConsumptionType } from '@allesauto/utils/consts';
import { formatPrice } from '@allesauto/utils/funcs';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useCallback } from 'react';
import { IntlShape } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import {
  deleteAllFilters,
  deleteFilterBodyColors,
  deleteFilterEmission,
  deleteFilterEquipment,
  deleteFilterFuel,
  deleteFilterInterior,
  deleteFilterMerchantLocation,
  deleteFilterPriceRange,
  deleteFilterVehicleCondition,
  deleteFilterVehicleType,
  FuelFilterStateProps,
  resetFilterLocation,
} from '@/store/redux/actions/pages/pcp';
import { deleteFilterDrive } from '@/store/redux/actions/pages/pcp/filters/drive';
import {
  IPCPFiltersState,
  PCPFilterState,
} from '@/store/redux/reducers/pages/pcp';
import { getDisplayNamesByName } from '@/store/redux/reducers/pages/pcp/facets';
import { BodyColorsStateProps } from '@/store/redux/reducers/pages/pcp/filters/bodyColors';
import { DriveStateProps } from '@/store/redux/reducers/pages/pcp/filters/drive';
import { EmissionFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/emission';
import { EquipmentFilterState } from '@/store/redux/reducers/pages/pcp/filters/equipment';
import { InteriorFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/interior';
import { LocationStateProps } from '@/store/redux/reducers/pages/pcp/filters/location';
import { MerchantLocationFilterState } from '@/store/redux/reducers/pages/pcp/filters/merchantLocation';
import { PriceFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/price';
import { VehicleConditionStateProps } from '@/store/redux/reducers/pages/pcp/filters/vehicleCondition';
import { VehicleTypeFilterStateProps } from '@/store/redux/reducers/pages/pcp/filters/vehicleType';
import {
  MakeModelFilterData,
  MakeModelFilterState,
  removeFilter as makeModelFilterRemoveFilter,
} from '@/store/redux/slices/pages/pcp/filters/makeModel';
import { IReduxActionProps } from '@/store/redux/Utils';

import FilterTags, { FilterTag } from '../components/FilterTags';
import { RouteFilter } from '../types';

/**
 * Filter Tag Container Types
 */
export type TagPayload = {
  [key: string]: string | number;
};
type Facets = SchemaTypes.Facets;
type FacetsKey = keyof Facets;
type FilterTagsContainerProps = {
  routeFilter?: RouteFilter;
  facets: Facets;
};
type DeleteActionWithoutPayload = () => IReduxActionProps;

/**
 * Filter Tag Container
 */
const FilterTagsContainer = ({
  routeFilter,
  facets,
}: FilterTagsContainerProps) => {
  let filterTags: FilterTag[];
  const deleteAll: () => IReduxActionProps = () => dispatch(deleteAllFilters());

  const dispatch = useDispatch();
  const filtersState = useSelector((state: any) => state.pages.pcp.filters);
  const allFiltersSetFlag = useSelector(
    (state: any) => state.pages.pcp.allFiltersSetFlag
  );
  const memoizedGetFilterTags = useCallback(getFilterTags, [
    JSON.stringify(filtersState),
  ]);
  const memoizedFilterTags = memoizedGetFilterTags(filtersState)(dispatch);

  // SSR and CSR with Routefilter
  // routeFilter e.g. { fuelType : { fuelType: ['Hybrid'] }
  if (!isEmpty(routeFilter)) {
    // routeFilterKey e.g. fuelType
    const routeFilterKey = Object.keys(routeFilter as RouteFilter)[0];
    // routeFilterPayload e.g. {fuelType: ['Hybrid'] }
    const routeFilterPayload = Object.values(routeFilter as RouteFilter)[0];
    // routerFilterValue e.g. Hybrid
    const routerFilterValue = Object.values(routeFilterPayload)[0][0];
    // Tag key-value payload for routeFilter
    const tagPayload = {
      [routeFilterKey]: routerFilterValue,
    } as TagPayload;

    // discrepency between routeFilter keys and facets keys
    const filterKey =
      routeFilterKey === 'vehicleCondition'
        ? 'vehicleConditions'
        : routeFilterKey;
    const SubFacets = facets[filterKey as FacetsKey] ?? {};
    const routerFilterSubKey = Object.keys(routeFilterPayload)[0];
    // discrepency between routeFilter keys and facets keys
    const subFilterKey =
      routerFilterSubKey === 'fuelType' ? 'fuelTypes' : routerFilterSubKey;

    // Displaynames by Facet Name/Value
    const displayNamesByName = getDisplayNamesByName(
      SubFacets[subFilterKey as keyof typeof SubFacets]
    );

    const ssrRouteFilterTag = {
      tagPayload,
      tagText: () => displayNamesByName[routerFilterValue],
      deleteTag: () => null,
    };

    // SSR with Routefilter
    if (typeof window === 'undefined') {
      filterTags = [ssrRouteFilterTag];
    }
    // CSR with Routefilter
    else {
      filterTags = allFiltersSetFlag ? memoizedFilterTags : [ssrRouteFilterTag];
    }
  }
  // CSR (and SSR) without RouteFilter (for SSR tag will always be empty if no route filter is present)
  else {
    filterTags = memoizedFilterTags;
  }

  return <FilterTags filterTags={filterTags} deleteAll={deleteAll} />;
};

export default FilterTagsContainer;

/**
 * Tag Container Utility Mapping Functions By FilterKey
 */

/**
 * Get Tag Text By FilterKey
 */
const tagTextByFilterKey =
  (filterKey: string) => (filterState: PCPFilterState) => {
    const facets = useSelector((state: any) => state.pages.pcp.facets);

    switch (filterKey) {
      case 'interior':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          const [payloadKey, payloadValue] = Object.entries(tagPayload)[0];
          const tagLabel = translate(
            intl,
            'ui.PCP.TAGS.INTERIOR_COLOR_TAG_LABEL'
          );

          return payloadKey === 'interiorColor'
            ? `${tagLabel} ${payloadValue}`
            : `${payloadValue}`;
        };
      case 'location':
        return (tagPayload: TagPayload) => (intl: IntlShape) =>
          'location' in filterState && filterState['location']
            ? `${filterState['radius']} ${translate(
                intl,
                'ui.PCP.TAGS.LOCATION_TAG_LABEL'
              )}${tagPayload.location}`
            : '';
      case 'vehicleType':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          switch (Object.keys(tagPayload)[0]) {
            case 'vehicleType':
              return `${
                facets[filterKey]['vehicleType'][tagPayload['vehicleType']]
              }`;
            case 'doors':
              return `${
                facets[filterKey]['doors'][tagPayload['doors']]
              } ${translate(intl, 'ui.PCP.TAGS.VEHICLE_TYPE_TAG_DOORS')}`;
            case 'minSeats':
              return 'maxSeats' in filterState && filterState['maxSeats']
                ? `${tagPayload.minSeats}-${
                    filterState['maxSeats']
                  } ${translate(intl, 'ui.PCP.TAGS.VEHICLE_TYPE_TAG_SEATS')}`
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_TYPE_TAG_SEATS_MIN'
                  )} ${tagPayload.minSeats} ${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_TYPE_TAG_SEATS'
                  )}`;
            case 'maxSeats':
              return 'minSeats' in filterState && filterState['minSeats']
                ? ''
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_TYPE_TAG_SEATS_MAX'
                  )} ${tagPayload.maxSeats} ${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_TYPE_TAG_SEATS'
                  )}`;
            default:
              return String(Object.values(tagPayload)[0]);
          }
        };
      case 'vehicleCondition':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          switch (Object.keys(tagPayload)[0]) {
            case 'condition':
              return `${
                facets[filterKey]['condition'][tagPayload['condition']]
              }`;
            case 'mileage':
              return `${
                facets[filterKey]['mileage'][tagPayload['mileage']]
              } ${translate(
                intl,
                'ui.PCP.TAGS.VEHICLE_CONDITION_TAG_MILEAGE_UNIT'
              )}`;
            case 'minYear':
              return 'maxYear' in filterState && filterState['maxYear']
                ? `${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_CONDITION_TAG_YEARS'
                  )} ${tagPayload.minYear}-${filterState['maxYear']}`
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_CONDITION_TAG_YEARS'
                  )} ${tagPayload.minYear}`;
            case 'maxYear':
              return 'minYear' in filterState && filterState['minYear']
                ? ''
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.VEHICLE_CONDITION_TAG_YEARS'
                  )} ${tagPayload.maxYear}`;
            case 'maxOwners':
              return `max. ${tagPayload.maxOwners} ${translate(
                intl,
                'ui.PCP.TAGS.VEHICLE_CONDITION_TAG_MAX_OWNERS'
              )}`;
            default:
              return String(Object.values(tagPayload)[0]);
          }
        };
      case 'drive':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          const [payloadKey, payloadValue] = Object.entries(tagPayload)[0];

          // gearbox case
          if (payloadKey === 'gearBox') {
            return String(Object.values(tagPayload)[0]);
          }

          // power and maxCubicCapacity case
          const powerUnit =
            'power' in filterState && filterState.power
              ? translate(intl, `ui.PCP.FILTERS.DRIVE.${filterState.power.key}`)
              : 'PS';

          const unit =
            payloadKey === 'power' ? powerUnit : consts.UNITS.CUBIC_CAPACITY;

          return `${payloadValue} ${unit}`;
        };
      case 'emission':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          switch (Object.keys(tagPayload)[0]) {
            case 'emissionClass':
              return `${translate(
                intl,
                'ui.PCP.TAGS.EMISSION_EMISSIONCLASS'
              )} ${
                facets[filterKey]['emissionClass'][tagPayload['emissionClass']]
              }`;
            case 'emissionSticker':
              return `${translate(
                intl,
                'ui.PCP.TAGS.EMISSION_EMISSIONSTICKER'
              )} ${
                facets[filterKey]['emissionSticker'][
                  tagPayload['emissionSticker']
                ]
              }`;
            case 'particleFilter':
              return 'particleFilter' in filterState &&
                filterState['particleFilter']
                ? `${translate(intl, 'ui.PCP.TAGS.EMISSION_PARTICLE_FILTER')}`
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.EMISSION_NO_PARTICLE_FILTER'
                  )}`;
            case 'maxCo2Emission':
              return `${translate(
                intl,
                'ui.PCP.TAGS.EMISSION_MAXC02EMISSION_MAX'
              )} ${tagPayload.maxCo2Emission} ${translate(
                intl,
                'ui.PCP.TAGS.EMISSION_MAXC02EMISSION_UNIT'
              )}`;
            case 'co2Efficiency':
              return `${translate(
                intl,
                'ui.PCP.TAGS.EMISSION_EMISSIONCLASS'
              )} ${
                facets[filterKey]['co2Efficiency'][tagPayload['co2Efficiency']]
              }`;
            default:
              return String(Object.values(tagPayload)[0]);
          }
        };
      case 'priceRange':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          const [payloadKey, payloadValue] = Object.entries(tagPayload)[0];
          const priceRangeDefaultsByFinanceType = facets['priceRange'];

          switch (payloadKey) {
            case 'key':
              return ''; // dont show price finance type as per ux
            case 'min': {
              const priceFinanceType =
                'key' in filterState && filterState['key'];

              const defaultMin =
                'key' in filterState &&
                priceFinanceType &&
                priceRangeDefaultsByFinanceType &&
                priceRangeDefaultsByFinanceType[priceFinanceType].min;
              const defaultMax =
                'key' in filterState &&
                priceFinanceType &&
                priceRangeDefaultsByFinanceType &&
                priceRangeDefaultsByFinanceType[priceFinanceType].max;

              if (payloadValue === defaultMin) return '';

              return 'max' in filterState &&
                filterState['max'] &&
                'key' in filterState &&
                priceFinanceType &&
                filterState['max'] !== defaultMax
                ? `${translate(
                    intl,
                    `ui.PCP.TAGS.PRICE_RANGE_${priceFinanceType}_LABEL`
                  )} ${intl.formatNumber(
                    tagPayload.min as number
                  )}-${formatPrice(intl, filterState['max'])}`
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.PRICE_RANGE_FROM'
                  )} ${translate(
                    intl,
                    `ui.PCP.TAGS.PRICE_RANGE_${priceFinanceType}_LABEL`
                  )} ${formatPrice(intl, tagPayload.min as number)}`;
            }
            case 'max': {
              const priceFinanceType =
                'key' in filterState && filterState['key'];

              const defaultMin =
                priceFinanceType &&
                priceRangeDefaultsByFinanceType[priceFinanceType].min;
              const defaultMax =
                priceFinanceType &&
                priceRangeDefaultsByFinanceType[priceFinanceType].max;

              if (payloadValue === defaultMax) return '';

              return 'min' in filterState &&
                filterState['min'] &&
                priceFinanceType &&
                filterState['min'] !== defaultMin &&
                tagPayload.max
                ? ''
                : `${translate(
                    intl,
                    'ui.PCP.TAGS.PRICE_RANGE_TILL'
                  )} ${translate(
                    intl,
                    `ui.PCP.TAGS.PRICE_RANGE_${priceFinanceType}_LABEL`
                  )} ${formatPrice(intl, tagPayload.max as number)}`;
            }
            default:
              return String(payloadValue);
          }
        };
      case 'fuelType':
        return (tagPayload: TagPayload) => (intl: IntlShape) => {
          const [payloadKey, name] = Object.entries(tagPayload)[0];
          // consumption case
          if (
            payloadKey === 'maxFuelConsumption' ||
            payloadKey === 'maxPowerConsumption'
          ) {
            return `${translate(intl, 'ui.PCP.TAGS.CONSUMPTION_TAG_LABEL')}${
              Object.values(tagPayload)[0]
            }${
              payloadKey === 'maxFuelConsumption'
                ? ConsumptionType.LITERS_PER_KM
                : ConsumptionType.KWH
            }`;
          }
          // fuelType case
          return facets[filterKey][payloadKey]
            ? facets[filterKey][payloadKey][name]
            : name;
        };
      case 'makeModel':
        return (tagPayload: TagPayload) => () => {
          const selectedVehicle =
            'data' in filterState &&
            filterState['data'][tagPayload.filterIndex as number];

          if (selectedVehicle) {
            const selectedModelGroup = selectedVehicle.models.data.find(
              (model) => model.name === selectedVehicle.models.selected
            );

            if (selectedModelGroup) {
              // Use the translated text for selectedModelGroup
              return `${selectedVehicle.makes.selected} ${
                selectedModelGroup.displayName ?? ''
              }`;
            } else {
              return `${selectedVehicle.makes.selected} ${
                selectedVehicle.models.selected ?? ''
              }`;
            }
          }
        };
      default:
        // cases with no custom labels or custom grouping
        // e.g. bodyColors, interior, equipment
        return (tagPayload: TagPayload) => () => {
          const [payloadKey, name] = Object.entries(tagPayload)[0];
          const displayNameByName = facets[filterKey][payloadKey];

          return displayNameByName ? displayNameByName[name] : name;
        };
    }
  };

/**
 * Delete Tag Action Payloads (Key-Value Pairs) by Filter State
 */
const tagPayloadsByFilterKey = {
  location: (filterState: LocationStateProps): TagPayload[] =>
    filterState['location'] && 'label' in filterState['location']
      ? [
          {
            location: filterState['location']['label'],
          },
        ]
      : [],
  merchantLocation: (
    filterState: MerchantLocationFilterState
  ): TagPayload[] => [
    ...(filterState['merchantLocation']
      ? filterState['merchantLocation'].map((merchantLocation) => ({
          merchantLocation,
        }))
      : []),
  ],
  vehicleType: (filterState: VehicleTypeFilterStateProps): TagPayload[] => [
    ...filterState['vehicleType'].map((vehicleType: string) => ({
      vehicleType,
    })),
    ...filterState['doors'].map((doors: string) => ({
      doors,
    })),
    ...(filterState['minSeats'] ? [{ minSeats: filterState['minSeats'] }] : []),
    ...(filterState['maxSeats'] ? [{ maxSeats: filterState['maxSeats'] }] : []),
  ],
  bodyColors: (filterState: BodyColorsStateProps): TagPayload[] =>
    filterState['colors'].map((color: string) => ({ color })),
  drive: (filterState: DriveStateProps): TagPayload[] => [
    ...filterState['gearBox'].map((gearBox) => ({ gearBox })),
    ...(!isNil(filterState['power'].min) && !isNil(filterState['power'].max)
      ? [{ power: `${filterState['power'].min}-${filterState['power'].max}` }]
      : []),
    ...(!isNil(filterState['maxCubicCapacity'])
      ? [{ maxCubicCapacity: filterState['maxCubicCapacity'] }]
      : []),
  ],
  fuelType: (filterState: FuelFilterStateProps): TagPayload[] => [
    ...(filterState['fuelType']
      ? filterState['fuelType'].map((fuelType) => ({ fuelType }))
      : []),
    ...(!isNil(filterState['maxFuelConsumption'])
      ? [{ maxFuelConsumption: filterState['maxFuelConsumption'] }]
      : []),
    ...(!isNil(filterState['maxPowerConsumption'])
      ? [{ maxPowerConsumption: filterState['maxPowerConsumption'] }]
      : []),
  ],
  interior: (filterState: InteriorFilterStateProps): TagPayload[] => [
    ...(filterState['interiorType']
      ? filterState['interiorType'].map((interiorType) => ({ interiorType }))
      : []),
    ...(filterState['interiorColor']
      ? filterState['interiorColor'].map((interiorColor) => ({ interiorColor }))
      : []),
  ],
  equipment: (filterState: EquipmentFilterState): TagPayload[] => [
    ...(filterState['aircon']
      ? filterState['aircon'].map((aircon) => ({ aircon }))
      : []),
    ...(filterState['comfort']
      ? filterState['comfort'].map((comfort) => ({ comfort }))
      : []),
    ...(filterState['security']
      ? filterState['security'].map((security) => ({ security }))
      : []),
    ...(filterState['entertainment']
      ? filterState['entertainment'].map((entertainment) => ({
          entertainment,
        }))
      : []),
  ],
  emission: (filterState: EmissionFilterStateProps): TagPayload[] => [
    ...(filterState['emissionClass']
      ? [{ emissionClass: filterState['emissionClass'] }]
      : []),
    ...(filterState['emissionSticker']
      ? filterState['emissionSticker'].map((emissionSticker) => ({
          emissionSticker,
        }))
      : []),
    ...(filterState['particleFilter']
      ? [
          {
            // value unused, parsed to string in order to be a valid index type
            particleFilter: String(filterState['particleFilter']),
          },
        ]
      : []),
    ...(filterState['maxCo2Emission'] !== null &&
    filterState['maxCo2Emission'] !== undefined
      ? [{ maxCo2Emission: filterState['maxCo2Emission'] }]
      : []),
    ...(filterState['co2Efficiency']
      ? [{ co2Efficiency: filterState['co2Efficiency'] }]
      : []),
  ],
  priceRange: (filterState: PriceFilterStateProps): TagPayload[] => [
    ...(filterState['key'] ? [{ key: filterState['key'] }] : []),
    ...(filterState['min'] ? [{ min: filterState['min'] }] : []),
    ...(filterState['max'] ? [{ max: filterState['max'] }] : []),
  ],
  makeModel: (filterState: MakeModelFilterState): TagPayload[] =>
    filterState['data'].reduce<
      {
        make?: string;
        filterIndex: number;
      }[]
    >(
      (
        acc: {
          filterIndex: number;
        }[],
        { makes }: MakeModelFilterData,
        filterIndex
      ) => [...acc, ...(makes.selected ? [{ filterIndex }] : [])],
      []
    ), // [{make, filterIndex}, {model,filterIndex}]
  vehicleCondition: (filterState: VehicleConditionStateProps): TagPayload[] => [
    ...(filterState['condition']
      ? filterState['condition'].map((condition) => ({ condition }))
      : []),
    ...(filterState['mileage'] ? [{ mileage: filterState['mileage'] }] : []),
    ...(filterState['minYear'] ? [{ minYear: filterState['minYear'] }] : []),
    ...(filterState['maxYear'] ? [{ maxYear: filterState['maxYear'] }] : []),
    ...(filterState['maxOwners'] !== null &&
    filterState['maxOwners'] !== undefined
      ? [{ maxOwners: filterState['maxOwners'] }]
      : []),
  ],
};

const deleteActionByFilterKey = {
  location: resetFilterLocation,
  merchantLocation: deleteFilterMerchantLocation,
  bodyColors: deleteFilterBodyColors,
  equipment: deleteFilterEquipment,
  emission: deleteFilterEmission,
  priceRange: deleteFilterPriceRange,
  makeModel: makeModelFilterRemoveFilter,
  drive: deleteFilterDrive,
  interior: deleteFilterInterior,
  fuelType: deleteFilterFuel,
  vehicleCondition: deleteFilterVehicleCondition,
  vehicleType: deleteFilterVehicleType,
};

const tagWithPayload = {
  location: false,
  merchantLocation: true,
  bodyColors: true,
  equipment: true,
  emission: true,
  priceRange: true,
  makeModel: true,
  drive: true,
  interior: true,
  fuelType: true,
  vehicleCondition: true,
  vehicleType: true,
};

/**
 * Get Filter Tags from filterState
 */
const getFilterTags =
  (filtersState: IPCPFiltersState) =>
  (dispatch: Dispatch): FilterTag[] =>
    Object.entries(filtersState).reduce(
      (acc: FilterTag[], currEntry: [string, any]) => {
        const [filterKey, filterState] = currEntry;

        const getTagText =
          tagTextByFilterKey(filterKey) &&
          tagTextByFilterKey(filterKey)(filterState);

        const tagPayloads =
          tagPayloadsByFilterKey[filterKey as keyof IPCPFiltersState] &&
          tagPayloadsByFilterKey[filterKey as keyof IPCPFiltersState](
            filterState
          );

        const deleteAction =
          deleteActionByFilterKey[filterKey as keyof IPCPFiltersState];

        const isPayloadAction =
          tagWithPayload[filterKey as keyof IPCPFiltersState];

        return [
          ...acc,
          ...(tagPayloads
            ? tagPayloads.map((tagPayload) => ({
                ...(deleteAction && {
                  deleteTag: isPayloadAction
                    ? () =>
                        dispatch(
                          deleteAction(tagPayload as any) as IReduxActionProps
                        )
                    : () =>
                        dispatch(
                          (deleteAction as DeleteActionWithoutPayload)()
                        ),
                }),
                tagText: getTagText(tagPayload),
                tagPayload,
              }))
            : []),
        ];
      },
      []
    );
